/**
 * Server / webhook error bodies: `{ "code": 400, "message": "..." }`
 * or `[{ "code": 400, "message": "..." }]`.
 * Use `errorMessageFromBody` when parsing incoming error JSON.
 * Use `apiErrorJson` when returning errors from Next.js API routes.
 */

/** Webhook errors may be a single object or a one-element (or more) array of error objects. */
export function normalizeWebhookPayload(json: unknown): Record<string, unknown> | null {
  if (Array.isArray(json)) {
    if (json.length === 0) return null;
    const first = json[0];
    if (typeof first === "object" && first !== null && !Array.isArray(first)) {
      return first as Record<string, unknown>;
    }
    return null;
  }
  if (typeof json === "object" && json !== null && !Array.isArray(json)) {
    return json as Record<string, unknown>;
  }
  return null;
}

function coerceErrorRecord(
  data: Record<string, unknown> | null | undefined | unknown,
): Record<string, unknown> | null {
  if (data === null || data === undefined) return null;
  return normalizeWebhookPayload(data) ?? null;
}

export function errorMessageFromBody(
  data: Record<string, unknown> | null | undefined | unknown,
  fallback: string,
): string {
  const rec = coerceErrorRecord(data);
  if (!rec) return fallback;

  const msg = rec.message;
  if (typeof msg === "string" && msg.trim()) return msg.trim();

  const rawErr = rec.error;
  if (typeof rawErr === "string" && rawErr.trim()) return rawErr.trim();
  if (typeof rawErr === "object" && rawErr !== null) {
    const obj = rawErr as Record<string, unknown>;
    const parts: string[] = [];
    for (const [field, value] of Object.entries(obj)) {
      if (Array.isArray(value) && value.length > 0 && typeof value[0] === "string") {
        parts.push(`${field}: ${value[0] as string}`);
      }
    }
    if (parts.length > 0) return parts.join(". ");
  }

  return fallback;
}

/** Numeric `code` from an error body, if present. */
export function errorCodeFromBody(
  data: Record<string, unknown> | undefined | unknown,
): number | undefined {
  const rec = coerceErrorRecord(data) ?? undefined;
  const c = rec?.code;
  return typeof c === "number" && Number.isFinite(c) ? c : undefined;
}

/**
 * Some webhooks return HTTP 200 with `{ "code": 400, "message": "..." }` for failures.
 * Treat `code >= 400` in the JSON body as an error even when `res.ok` is true.
 */
export function isApplicationErrorPayload(data: Record<string, unknown>): boolean {
  const c = data.code;
  return typeof c === "number" && c >= 400;
}

/** Standard error JSON for API routes (errors only — not for success payloads). */
export function apiErrorJson(
  message: string,
  httpStatus: number,
  bodyCode?: number,
): { code: number; message: string } {
  const code =
    typeof bodyCode === "number" && Number.isFinite(bodyCode) ? bodyCode : httpStatus;
  return { code, message };
}

/** Map webhook `code` to a valid HTTP error status when possible. */
export function httpStatusFromErrorCode(code: number | undefined, fallback: number): number {
  if (typeof code === "number" && code >= 400 && code < 600) return code;
  return fallback;
}
